import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, Space, Input, InputRef, Button, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getEvent } from "../../services/EventService";
import { formatDate } from "../../core/Utility";
import "./FinishedEventsComparationPage.scss";
import { EventDto, UpdateEventDto } from "../../models/Event";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { Chart } from "primereact/chart";

interface DataSet {
  label: string;
  backgroundColor: string;
  borderColor: string;
  data: string[];
}

const FinishedEventsComparationPage = () => {
  const { setBreadcrumb } = useApplicationContext();
  const [allCompareEvent, setAllCompareEvent] = useState<EventDto[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);
  const location = useLocation();
  const eventIds: { id: string }[] = location.state;
  const [datasets, setDatasets] = useState<DataSet[]>();
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumb(["Statistika", "Završeni događaji", "Poređenje"]);
    let updatedCompareEvent = [];
    setActiveSpinner(true);

    Promise.all(
      eventIds.map(async (item, i) => {
        const res = await getEvent(item.id);
        return { ...res.data.result, key: i + 1 };
      })
    )
      .then((results) => {
        updatedCompareEvent = results;
        const documentStyle = getComputedStyle(document.documentElement);

        let dataSet = results?.map((event, i) => {
          let colors = [
            "--primary-800",
            "--red-700",
            "--teal-800",
            "--bluegray-800",
            "--orange-800",
          ];
          return {
            label: `${event?.name}`,
            backgroundColor: documentStyle.getPropertyValue(colors[i]),
            borderColor: documentStyle.getPropertyValue(colors[i]),
            data: [
              event?.allRegistrationNumber,
              event.allParticipantNumber,
              event.allRegistrationRejected,
            ],
          };
        }) as DataSet[];

        setDatasets(dataSet);
        setAllCompareEvent(updatedCompareEvent);
        setActiveSpinner(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [eventIds]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  type DataIndex = keyof EventDto;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<EventDto> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži po imenu`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (
      value,
      record //@ts-ignore
    ) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<EventDto> = [
    {
      title: "Naziv događaja",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="blue-text">{name}</span>,
      sorter: (a, b) => (a.name < b.name ? -1 : 1),
      ...getColumnSearchProps("name"),
    },
    {
      title: "Mesto događaja",
      dataIndex: "place",
      key: "place",
      render: (place) => <span className="blue-text">{place}</span>,
      sorter: (a, b) => (a.place < b.place ? -1 : 1),
      ...getColumnSearchProps("place"),
    },
    {
      title: "Datum početka",
      dataIndex: "dateFrom",
      key: "dateFrom",
      align: "center",
      // defaultSortOrder: "descend",
      sorter: (a, b) =>
        new Date(a?.dateFrom).getTime() < new Date(b?.dateFrom).getTime()
          ? -1
          : 1,
      render: (dateFrom) => (
        <span className="blue-text">
          {formatDate(new Date(dateFrom as Date))}
        </span>
      ),
    },
    {
      title: "Datum završetka",
      dataIndex: "dateTo",
      key: "dateTo",
      align: "center",
      render: (dateTo) => (
        <span className="blue-text">
          {formatDate(new Date(dateTo as Date))}
        </span>
      ),
    },
    {
      title: "Broj prijavljenih",
      dataIndex: "allRegistrationNumber",
      key: "allRegistrationNumber",
      align: "center",
      sorter: (a, b) =>
        a.allRegistrationNumber < b.allRegistrationNumber ? -1 : 1,
      render: (allRegistrationNumber, id) => (
        <span
          className="blue-text"
          style={{ cursor: "pointer" }}
          onClick={() => {
            return navigate("/events-comparation/participants", {
              state: { type: "registrations", eventId: id.id },
            });
          }}
        >
          {allRegistrationNumber}
        </span> //TODO...
      ),
    },
    {
      title: "Broj učestvovanih",
      dataIndex: "allParticipantNumber",
      key: "allParticipantNumber",
      align: "center",
      sorter: (a, b) =>
        a.allParticipantNumber < b.allParticipantNumber ? -1 : 1,
      render: (allParticipantNumber, id) => (
        <span
          className="blue-text"
          style={{ cursor: "pointer" }}
          onClick={() => {
            return navigate("/events-comparation/participants", {
              state: { type: "participants", eventId: id.id },
            });
          }}
        >
          {allParticipantNumber}
        </span> //TODO...
      ),
    },
    {
      title: "Broj odbijenih",
      dataIndex: "allRegistrationRejected",
      key: "allRegistrationRejected",
      align: "center",
      sorter: (a, b) =>
        a.allRegistrationRejected < b.allRegistrationRejected ? -1 : 1,
      render: (allRegistrationRejected, id) => (
        <span
          className="blue-text"
          style={{ cursor: "pointer" }}
          onClick={() => {
            return navigate("/events-comparation/participants", {
              state: { type: "rejected", eventId: id.id },
            });
          }}
        >
          {allRegistrationRejected}
        </span> //TODO...
      ),
    },
    {
      title: "Broj otkazanih",
      dataIndex: "deletedRegistrations",
      key: "deletedRegistrations",
      align: "center",
      sorter: (a, b) =>
        a.deletedRegistrations < b.deletedRegistrations ? -1 : 1,
      render: (deletedRegistrations, id) => (
        <span
          className="blue-text"
          style={{ cursor: "pointer" }}
          onClick={() => {
            return navigate("/events-comparation/participants", {
              state: { type: "deleted", eventId: id.id },
            });
          }}
        >
          {deletedRegistrations}
        </span> //TODO...
      ),
    },
    {
      title: "Ukupna zarada",
      dataIndex: "allEarnings",
      key: "allEarnings",
      align: "center",
      sorter: (a, b) => (a.allEarnings < b.allEarnings ? -1 : 1),
      render: (allEarnings) => (
        <span className="blue-text">{allEarnings} RSD.</span> //TODO...
      ),
    },
  ];

  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue("--text-color");
    const textColorSecondary = documentStyle.getPropertyValue(
      "--text-color-secondary"
    );
    const surfaceBorder = documentStyle.getPropertyValue("--surface-border");
    const data = {
      labels: ["Prijavljeni", "Učestvovali", "Odbijeni"],
      datasets: datasets,
    };
    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            fontColor: textColor,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              weight: 500,
            },
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [allCompareEvent]);

  return (
    <PageLayout>
      <Spin spinning={activeSpinner}>
        <Table
          sticky={true}
          columns={columns}
          // expandable={{
          //   expandedRowRender: (record) => (
          //     <p style={{ margin: 0 }}>{record.description}</p>
          //   ),
          //   // rowExpandable: (record) => record.name !== "Not Expandable",
          // }}
          dataSource={allCompareEvent}
          pagination={false}
        />
        {allCompareEvent.length > 0 && (
          <div className="chart-event">
            <Chart type="bar" data={chartData} options={chartOptions} />
          </div>
        )}
      </Spin>
    </PageLayout>
  );
};

export default FinishedEventsComparationPage;
